/**
 * Filter Mappings Configuration
 * Maps tour names (from Rezdy) to their assigned filters based on CSV data
 * 
 * Filters order: All, Full Day, Short Walks, Private, Tongariro, Ruapehu
 */

export type FilterType = 'All' | 'Full Day' | 'Short Walks' | 'Private' | 'Tongariro' | 'Ruapehu';

export interface FilterMapping {
  tourName: string;
  productCode?: string; // Optional product code for reference
  filters: FilterType[];
}

/**
 * Filter order as specified in CSV
 */
export const FILTER_ORDER: FilterType[] = ['All', 'Full Day', 'Short Walks', 'Private', 'Tongariro', 'Ruapehu'];

/**
 * Filter mappings based on CSV data
 * Maps exact tour names from Rezdy to their assigned filters
 */
const filterMappings: Record<string, FilterType[]> = {
  // All tours have "All" filter, plus specific filters
  '2 Hour Pre Sunset Guided Group Walk': ['All', 'Short Walks'],
  'Premium Tongariro Crossing Guided Group Walk': ['All', 'Full Day', 'Tongariro'],
  'Half Day Tongariro Alpine Guided Group Walk': ['All', 'Short Walks', 'Tongariro'],
  'Private Tongariro Crossing Guided Walk': ['All', 'Private', 'Tongariro', 'Full Day'],
  'Premium Mt Ruapehu Summit Plateau Guided Group Hike': ['All', 'Ruapehu', 'Full Day'],
  'Private Tama Lakes Guided Walk': ['All', 'Private', 'Full Day'],
  'Private Mt Ruapehu Summit Plateau Guided hike': ['All', 'Ruapehu', 'Full Day', 'Private'],
  'Private Half Day Tongariro Alpine Guided Hike': ['All', 'Private'],
  'Private 1 Day Whanganui River Guided Canoe Tour (Taumarunui to Ohinepane)': ['All', 'Full Day', 'Private'],
  
  // Tours that only have "All" filter (from CSV - no specific filter columns)
  'Cheeky Midweeky': ['All'],
  '$150 Adrift Tongariro Gift Card': ['All'],
  '$200 Adrift Tongariro Gift Card': ['All'],
  '$250 Adrift Tongariro Gift Card': ['All'],
  '$300 Adrift Tongariro Gift Card': ['All'],
  '2 Hour Guided Day Walk': ['All'],
  '3 Day Northern Circuit Guided Trek': ['All'],
  '3 Day Whanganui River Guided Canoe Group Tour (Whakahoro to Pipiriki)': ['All'],
  'Alpine Chalets Unit': ['All'],
  'Basic Winter Only Tongariro Crossing Guided Group Walk': ['All'],
  'Coach Tour 1 Day Whanganui River Guided Canoe (Taumarunui to Ohinepane)': ['All'],
  'Coach Tour Tongariro Crossing Guided Walk': ['All'],
  'Coach Tour Transport Only': ['All'],
  'Gear Hire': ['All'],
  'Guide Only for group bookings': ['All'],
  'Packed Lunch': ['All'],
  'School Camps': ['All'],
  'TOURPLAN 2 Hour Pre Sunset Guided Group Walk ** TOUR ONLY**': ['All'],
  'TOURPLAN Half Day Tongariro Alpine Guided Group Walk **TOUR ONLY**': ['All'],
  'TOURPLAN Premium Mt Ruapehu Summit Plateau Guided Group Hike **TOUR ONLY**': ['All'],
  'TOURPLAN Premium Tongariro Crossing Guided Group Walk **INCL LUNCH**': ['All'],
  'TOURPLAN Premium Tongariro Crossing Guided Group Walk **TOUR ONLY**': ['All'],
  'TOURPLAN Private Tongariro Crossing Guided Walk **TOUR ONLY**': ['All'],
  'Transfer Surcharge': ['All'],
  
  // Note: "Basic Mt Ruapehu Summit Plateau Guided Group Hike" from CSV matches 
  // "Premium Mt Ruapehu Summit Plateau Guided Group Hike" in Rezdy (fuzzy match)
  // Using the Premium version's mapping
};

/**
 * Get filters for a tour by name
 * Returns array of FilterType that the tour belongs to
 */
export function getFiltersForTour(tourName: string): FilterType[] {
  const normalizedName = tourName.trim();
  
  // Try exact match first
  if (filterMappings[normalizedName]) {
    return filterMappings[normalizedName];
  }
  
  // Try case-insensitive match
  const lowerName = normalizedName.toLowerCase();
  for (const [key, filters] of Object.entries(filterMappings)) {
    if (key.toLowerCase() === lowerName) {
      return filters;
    }
  }
  
  // Default: all tours belong to "All" filter
  return ['All'];
}

/**
 * Get all tours that belong to a specific filter
 * Note: This requires knowledge of all tour names, so it's mainly for display purposes
 */
export function getToursForFilter(filter: FilterType): string[] {
  const tours: string[] = [];
  
  for (const [tourName, filters] of Object.entries(filterMappings)) {
    if (filters.includes(filter)) {
      tours.push(tourName);
    }
  }
  
  return tours;
}

/**
 * Get all filter assignments
 * Returns array of FilterMapping objects
 */
export function getAllFilterAssignments(): FilterMapping[] {
  const assignments: FilterMapping[] = [];
  
  for (const [tourName, filters] of Object.entries(filterMappings)) {
    assignments.push({
      tourName,
      filters
    });
  }
  
  return assignments;
}

/**
 * Check if a tour name matches any filter
 */
export function tourMatchesFilter(tourName: string, filter: FilterType): boolean {
  if (filter === 'All') {
    return true; // All tours match "All" filter
  }
  
  const tourFilters = getFiltersForTour(tourName);
  return tourFilters.includes(filter);
}

/**
 * List of tours that should be hidden from availability results
 * These are tours with only "All" filter, except "Cheeky Midweeky" which should still be shown
 */
const HIDDEN_TOURS = [
  '$150 Adrift Tongariro Gift Card',
  '$200 Adrift Tongariro Gift Card',
  '$250 Adrift Tongariro Gift Card',
  '$300 Adrift Tongariro Gift Card',
  '2 Hour Guided Day Walk',
  '3 Day Northern Circuit Guided Trek',
  '3 Day Whanganui River Guided Canoe Group Tour (Whakahoro to Pipiriki)',
  'Alpine Chalets Unit',
  'Basic Winter Only Tongariro Crossing Guided Group Walk',
  'Coach Tour 1 Day Whanganui River Guided Canoe (Taumarunui to Ohinepane)',
  'Coach Tour Tongariro Crossing Guided Walk',
  'Coach Tour Transport Only',
  'Gear Hire',
  'Guide Only for group bookings',
  'Packed Lunch',
  'School Camps',
  'TOURPLAN 2 Hour Pre Sunset Guided Group Walk ** TOUR ONLY**',
  'TOURPLAN Half Day Tongariro Alpine Guided Group Walk **TOUR ONLY**',
  'TOURPLAN Premium Mt Ruapehu Summit Plateau Guided Group Hike **TOUR ONLY**',
  'TOURPLAN Premium Tongariro Crossing Guided Group Walk **INCL LUNCH**',
  'TOURPLAN Premium Tongariro Crossing Guided Group Walk **TOUR ONLY**',
  'TOURPLAN Private Tongariro Crossing Guided Walk **TOUR ONLY**',
  'Transfer Surcharge'
];

/**
 * Check if a tour should be hidden from availability results
 * Hidden tours are those with only "All" filter, except "Cheeky Midweeky"
 */
export function isTourHidden(tourName: string): boolean {
  const normalizedName = tourName.trim();
  
  // Check exact match
  if (HIDDEN_TOURS.includes(normalizedName)) {
    return true;
  }
  
  // Check case-insensitive match
  const lowerName = normalizedName.toLowerCase();
  return HIDDEN_TOURS.some(hidden => hidden.toLowerCase() === lowerName);
}

/**
 * Get available filters (excluding "All" if needed)
 */
export function getAvailableFilters(includeAll: boolean = true): FilterType[] {
  if (includeAll) {
    return [...FILTER_ORDER];
  }
  return FILTER_ORDER.filter(f => f !== 'All');
}

/**
 * CSV order for tours (based on order in first column of CSV)
 * Tours should be sorted according to this order when displayed
 */
const CSV_TOUR_ORDER: string[] = [
  '2 Hour Pre Sunset Guided Group Walk',
  'Half Day Tongariro Alpine Guided Group Walk',
  'Premium Tongariro Crossing Guided Group Walk',
  'Premium Mt Ruapehu Summit Plateau Guided Group Hike',
  'Private Tongariro Crossing Guided Walk',
  'Private Half Day Tongariro Alpine Guided Hike',
  'Private Tama Lakes Guided Walk',
  'Private Mt Ruapehu Summit Plateau Guided hike',
  'Private 1 Day Whanganui River Guided Canoe Tour (Taumarunui to Ohinepane)',
  'Cheeky Midweeky'
];

/**
 * Get sort order for a tour name based on CSV order
 * Returns the index in CSV order, or Infinity if not found (sorts to end)
 */
export function getTourSortOrder(tourName: string): number {
  const normalizedName = tourName.trim();
  
  // Try exact match
  const index = CSV_TOUR_ORDER.findIndex(name => name === normalizedName);
  if (index !== -1) {
    return index;
  }
  
  // Try case-insensitive match
  const lowerName = normalizedName.toLowerCase();
  const caseInsensitiveIndex = CSV_TOUR_ORDER.findIndex(name => name.toLowerCase() === lowerName);
  if (caseInsensitiveIndex !== -1) {
    return caseInsensitiveIndex;
  }
  
  // Not in CSV order - sort to end
  return Infinity;
}

